import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi, setSessionExpiredHandler, tokenStore } from './api'
import type { AuthUser } from '@/types/api'

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  /** True until the stored session has been read. Prevents a login-screen flash. */
  initialising: boolean
  login: (email: string, password: string) => Promise<void>
  /** Mentor sign-in. Email and password, tenant resolved from the email. */
  mentorLogin: (
    email: string,
    password: string,
    tenantSlug: string,
  ) => Promise<void>
  /**
   * Student sign-in, step two. Step one is `authApi.requestStudentOtp`,
   * which needs no session and so does not belong on this context.
   */
  studentLogin: (
    tenantSlug: string,
    phone: string,
    code: string,
  ) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Where the last signed-out student or mentor came in from.
 *
 * Survives the logout that clears the session, because the redirect happens
 * on the render AFTER the user is gone. Not a credential — the slug is public
 * in the merchant-page URL — so storing it reveals nothing.
 */
const LAST_ENTRANCE_KEY = 'mentor.lastEntrance'

/**
 * The login path to bounce a signed-out visitor to.
 *
 * `/login/<slug>` when we know the institute, `/login` otherwise. Exported
 * so the router's catch-all and the session-expiry path agree — two
 * different answers here is how someone ends up at the admin door.
 */
export function loginPathForLastEntrance(): string {
  try {
    const slug = localStorage.getItem(LAST_ENTRANCE_KEY)
    // A slug reaches this as a path segment. Anything with a slash or a
    // space is not one, and must not be pasted into a route.
    if (slug && /^[a-z0-9-]{2,100}$/i.test(slug)) return `/login/${slug}`
  } catch {
    // Unreadable storage — fall through to the platform entrance.
  }
  return '/login'
}

/** Remember an institute entrance seen in the URL, even before signing in. */
export function rememberEntrance(slug: string): void {
  try {
    localStorage.setItem(LAST_ENTRANCE_KEY, slug)
  } catch {
    // Nothing to do — the redirect degrades to `/login`.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initialising, setInitialising] = useState(true)
  /* Safe: `QueryClientProvider` wraps this provider in `main.tsx`. */
  const queryClient = useQueryClient()

  /*
    The current user, readable from a callback that was registered once.
    The session-expiry handler below needs it and cannot re-subscribe on
    every change without tearing down the API client's wiring.
  */
  const userRef = useRef<AuthUser | null>(null)
  userRef.current = user

  /**
   * Restore the session from storage on boot.
   *
   * *** THE TOKEN IS NOT VALIDATED HERE ***
   * Deliberately. Firing a verification request before the first render adds a
   * round trip to every page load, and the answer arrives anyway: the first
   * real API call either succeeds or 401s, and the client's refresh handles
   * it. A stale token costs one retry, not a broken session.
   */
  useEffect(() => {
    const session = tokenStore.get()
    if (session) setUser(session.user)
    setInitialising(false)
  }, [])

  /**
   * Sign out, and remember which door this person came in by.
   *
   * *** THE SLUG MUST BE READ BEFORE THE SESSION IS CLEARED ***
   * `setUser(null)` is what makes the router fall through to its catch-all,
   * and at that point the tenant is already gone — which is exactly why
   * logout used to land students on `/login`. So the entrance is resolved
   * here, while the user is still in hand, and parked where the login route
   * can read it after the re-render.
   *
   * Only for a STUDENT or MENTOR. An admin's entrance IS `/login`, and
   * sending them to `/login/<slug>` would be a different screen.
   */
  const logout = useCallback(() => {
    const slug = user?.tenant_slug
    const isStaff = user?.role === 'TENANT_ADMIN' || user?.role === 'SUPER_ADMIN'

    authApi.logout()

    try {
      if (slug && !isStaff) {
        localStorage.setItem(LAST_ENTRANCE_KEY, slug)
      } else {
        localStorage.removeItem(LAST_ENTRANCE_KEY)
      }
    } catch {
      // Private mode or blocked storage. The redirect falls back to
      // `/login`, which is where it went before this existed.
    }

    /*
      *** THE CACHE OUTLIVES THE SESSION OTHERWISE ***
      React Query keeps every answer in memory, and signing in as someone
      else in the same tab reuses it: the next account saw the previous
      one's data until each query refetched. In chat that was visible —
      `is_mine` is computed per caller, so every bubble sat on the wrong
      side for the ten seconds until the poll (reported 2026-11-02).

      It is not only cosmetic. One person's bookings, plan balance and
      conversations should not be rendered to another, however briefly.

      `clear()` rather than `invalidateQueries`: invalidation keeps the old
      data on screen while it refetches, which is exactly the frame to get
      rid of.
    */
    queryClient.clear()

    setUser(null)
  }, [user, queryClient])

  /**
   * Wire the API client's "session is unrecoverable" signal to React state.
   *
   * The client cannot import React, and a failed refresh happens deep inside a
   * fetch — this is how that reaches the UI as a clean logout rather than a
   * cascade of 401 errors on every panel.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      /*
        An expired session is a logout nobody asked for, and it must land in
        the same place a deliberate one does. Read through the ref: this
        effect runs once, so a captured `user` would be the one from boot —
        null — and every expiry would send a student to the admin door.
      */
      const current = userRef.current
      const isStaff =
        current?.role === 'TENANT_ADMIN' || current?.role === 'SUPER_ADMIN'
      if (current?.tenant_slug && !isStaff) {
        rememberEntrance(current.tenant_slug)
      }
      /* Same reason as the deliberate logout — see `logout` above. */
      queryClient.clear()
      setUser(null)
    })
  }, [queryClient])

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await authApi.login(email, password)
    setUser(tokens.user)
  }, [])

  const mentorLogin = useCallback(
    async (email: string, password: string, tenantSlug: string) => {
      const tokens = await authApi.mentorLogin(email, password, tenantSlug)
      setUser(tokens.user)
    },
    [],
  )

  const studentLogin = useCallback(
    async (tenantSlug: string, phone: string, code: string) => {
      const tokens = await authApi.verifyStudentOtp(tenantSlug, phone, code)
      setUser(tokens.user)
    },
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      initialising,
      login,
      mentorLogin,
      studentLogin,
      logout,
    }),
    [user, initialising, login, mentorLogin, studentLogin, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider')
  }
  return context
}
